import { HomeScrollContext, useHomeScrollOwner } from "@components/HomePage/scroll/useHomeScroll";
import { useHomeTitlebarOffset } from "@components/HomePage/useHomeTitlebarOffset";
import type { Breakpoint } from "@core-ui/constants/breakpoints";
import { useBreakpoint } from "@core-ui/hooks/useBreakpoint";
import t from "@ext/localization/locale/translate";
import { LegacyScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import type React from "react";

const homePageWrapperClassName = [
	"bg-primary-bg flex flex-col h-full overflow-auto font-[Roboto,sans-serif]",
	"[&_.shadow-scroll>div]:flex [&_.shadow-scroll>div]:flex-col [&>div:has(.scroll-area)]:h-full",
	"[&_.groups]:mt-[1.8rem] [&_:is(.groups,.top-menu,.bottom-info)]:w-full [&_:is(.groups,.top-menu,.bottom-info)]:mx-auto",
	"[&_[data-catalog-card]]:w-full [&_[data-folder]]:w-full",
].join(" ");

const breakpointClassNames: Record<Breakpoint, string> = {
	sm: [
		"[&_:is(.groups,.top-menu,.bottom-info)]:max-w-full [&_:is(.groups,.top-menu,.bottom-info)]:px-4",
		"[&_.group-container]:gap-5 [&_.group-content]:grid-cols-2 [&_.group-content]:gap-3",
		"[&_.top-shadow]:top-[calc(3.25rem+var(--home-titlebar-offset))] [&_.bottom-info]:!flex-col",
		"[&_[data-card]]:min-w-[165px]",
	].join(" "),
	md: [
		"[&_:is(.groups,.top-menu,.bottom-info)]:max-w-[902px] [&_:is(.groups,.top-menu,.bottom-info)]:px-6",
		"[&_.group-container]:gap-5 [&_.group-content]:grid-cols-4 [&_.group-content]:gap-3",
		"[&_.top-shadow]:top-[calc(3.25rem+var(--home-titlebar-offset))]",
		"[&_[data-card]]:min-w-[171px]",
	].join(" "),
	lg: [
		"[&_:is(.groups,.top-menu,.bottom-info)]:max-w-[1173px] [&_:is(.groups,.top-menu,.bottom-info)]:px-9",
		"[&_.group-container]:gap-6 [&_.group-content]:grid-cols-4 [&_.group-content]:gap-4",
		"[&_.top-shadow]:top-[calc(3.7rem+var(--home-titlebar-offset))]",
		"[&_[data-card]]:min-w-[188px]",
	].join(" "),
	xl: [
		"[&_:is(.groups,.top-menu,.bottom-info)]:max-w-[1144px] [&_:is(.groups,.top-menu,.bottom-info)]:px-9",
		"[&_.group-container]:gap-6 [&_.group-content]:grid-cols-4 [&_.group-content]:gap-4",
		"[&_.top-shadow]:top-[calc(3.7rem+var(--home-titlebar-offset))]",
		"[&_[data-card]]:min-w-[188px]",
	].join(" "),
	"2xl": [
		"[&_:is(.groups,.top-menu,.bottom-info)]:max-w-[1144px] [&_:is(.groups,.top-menu,.bottom-info)]:px-9",
		"[&_.group-container]:gap-6 [&_.group-content]:grid-cols-4 [&_.group-content]:gap-4",
		"[&_.top-shadow]:top-[calc(3.7rem+var(--home-titlebar-offset))]",
		"[&_[data-card]]:min-w-[188px]",
	].join(" "),
};

export const HomePageWrapper = ({ children }: { children: React.ReactNode }) => {
	const breakpoint = useBreakpoint();
	const { ref, scroll } = useHomeScrollOwner();
	const titlebarOffset = useHomeTitlebarOffset();
	return (
		<div
			className={`${homePageWrapperClassName} ${breakpointClassNames[breakpoint]}`}
			style={{ "--home-titlebar-offset": titlebarOffset } as React.CSSProperties}
		>
			<LegacyScrollShadowContainer
				className="flex flex-col shadow-scroll h-full"
				contentProps={{ role: "region", "aria-label": t("home") }}
				ref={ref}
				shadowTopClassName="top-shadow"
			>
				<HomeScrollContext.Provider value={scroll}>{children}</HomeScrollContext.Provider>
			</LegacyScrollShadowContainer>
		</div>
	);
};
